import { ReviewAndBalancePayment } from "components/chat/ReviewAndBalancePayment/ReviewAndBalancePayment";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { PlusIcon } from "components/icons/PlusIcon";
import { UploadFile } from "components/UploadFile/UploadFile";
import { TASK_REVIEW_DOCUMENT } from "constants/chatType";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setCustomAttachments,
  setCustomLinks,
  setDocumentReviewPayload,
  setTaskReviewBalancePaymentStep,
} from "store/slices/reviewAndBalancePayment";
import { openUploadCSVModal } from "store/slices/openItem";
import { RootState } from "store/store";
import {
  LocalFileOrInkleDocs,
  ReviewLink,
} from "types/Models/reviewDocumentAndBalancingPayment";
import { BalancePayment } from "components/BalancePayment/BalancePayment";
import { openRequestDocumentsModal } from "store/slices/requestDocumentsInChat";
import { useChannelStateContext } from "stream-chat-react";
import { useModal } from "hooks/useModal";
import { RequestSignModal } from "components/RequestSign/RequestSignModal";
import { DropFile } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { SensitiveDataRequestModal } from "components/SensitiveData/SensitiveData";
import { useGetAllSensitiveDataTypesQuery } from "apis/sensitiveData";
import { useGetTaskFromChannelIdQuery } from "store/apis/task";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { CaretLeft } from "components/icons/CaretLeft";
import { AttachCart } from "./AttachCart";
import { CreateDftDraft } from "components/MessageBubble/FilingDraft/CreateDftDraft";
import { RequestAccountantAccess } from "components/MessageBubble/RequestAccountantAccess";
import { ScheduleMeetingModal } from "components/MessageBubble/ScheduleMeetingModal";
import { ChatRequestAnswer } from "components/MessageBubble/ChatRequestAnswer/ChatRequestAnswer";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import BareEntityCombobox from "components/EntitySelector/BareEntityCombobox";
import { useGetGroupDataQuery } from "store/apis/teamSetting";

type MobileSection = "request" | "sensitive" | "review" | "cart" | null;

export const TaskActionsFromChat = () => {
  const { channel } = useChannelStateContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUploadFileModal, setShowUploadFileModal] = useState(false);
  const [showBalancingReviewModal, setShowBalancingReviewModal] =
    useState(false);
  const dispatch = useDispatch();
  const { channelTaskId } = useSelector((state: RootState) => state.chat);
  const { customAttachment } = useSelector(
    (state: RootState) => state.reviewAndBalancePayment
  );
  const [showBalancePayment, setShowBalancePayment] = useState(false);
  const requestAnswerModal = useModal();
  const accountantAccessModal = useModal();
  const scheduleMeetingModal = useModal();

  const {
    close: closeRequestSignModal,
    isOpen: isRequestSignModal,
    open: openRequestSignModal,
  } = useModal();
  const {
    close: closeSensitiveDataRequestModal,
    isOpen: isSensitiveDataRequestModal,
    open: openSensitiveDataRequestModal,
  } = useModal();
  const onFileSelect = (v: LocalFileOrInkleDocs[]) => {
    dispatch(setCustomAttachments(v));
    dispatch(
      setDocumentReviewPayload({
        document_operation: TASK_REVIEW_DOCUMENT,
      })
    );
    setShowDropdown(false);
    setShowUploadFileModal(false);
  };

  const onLinksChange = (links: ReviewLink[]) => {
    dispatch(setCustomLinks(links));
  };
  const reviewDraft = useModal();

  const { data: currentChannelData } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel.id!,
    },
    {
      skip: !channel.id,
    }
  );

  const closeReviewAndBalancePayment = () => {
    dispatch(setTaskReviewBalancePaymentStep(1));
    setShowBalancingReviewModal(false);
  };
  const [formId, setFormId] = useState("");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const entityPickerModal = useModal();
  const { data: formTypes } = useGetAllSensitiveDataTypesQuery();
  const { isAdmin } = useRoleBasedView();
  const isSmallScreen = useIsSmallScreen();
  const [mobileSection, setMobileSection] = useState<MobileSection>(null);
  const { entity, company_group } = currentChannelData || {};
  const { secondary_channel, unsorted_folder_id } = company_group || {};
  const channelGroupId = channel.data?.group_uuid as string | undefined;

  // Radix positions a submenu to the side of its parent and never shifts it
  // horizontally (Popper uses shift({ crossAxis: false })), and SubContent
  // takes no `side` prop. On a phone there is no room beside a 270px menu, so
  // mobile drills down inside the same panel instead of opening a submenu.
  const requestItemNodes = (
    <>
      <DropDown.Item
        onSelect={() =>
          dispatch(
            openRequestDocumentsModal({
              isModalOpen: true,
              channelId: channel?.id,
              groupId: channel.data?.group_uuid as string,
              entityId: channel.data?.entity_id as string,
            })
          )
        }
      >
        Document
      </DropDown.Item>
      <DropDown.Item onSelect={openRequestSignModal}>Sign</DropDown.Item>
      <DropDown.Item onSelect={requestAnswerModal.open}>
        Information
      </DropDown.Item>
      <DropDown.Item onSelect={accountantAccessModal.open} disabled={!entity}>
        Accountant access
      </DropDown.Item>
      <DropDown.Item onSelect={scheduleMeetingModal.open}>
        Schedule Meeting
      </DropDown.Item>
      {channelTaskId && (
        <DropDown.Item onSelect={() => setShowBalancePayment(true)}>
          Payment
        </DropDown.Item>
      )}
    </>
  );

  const sensitiveDataItemNodes = formTypes?.map(({ uuid, name }, index) => (
    <DropDown.Item
      key={index}
      onSelect={() => {
        setFormId(uuid);
        if (entity) {
          openSensitiveDataRequestModal();
        } else {
          setSelectedEntityId("");
          entityPickerModal.open();
        }
      }}
    >
      {name}
    </DropDown.Item>
  ));

  const reviewItemNodes = (
    <>
      <DropDown.Item onSelect={reviewDraft.open}>
        Draft filing (DFT)
      </DropDown.Item>
      {channelTaskId && (
        <DropDown.Item onSelect={() => setShowUploadFileModal(true)}>
          Other document
        </DropDown.Item>
      )}
    </>
  );

  const attachCartNode = (
    <AttachCart groupId={channel.data?.group_uuid as string} />
  );

  const hasAttachCart = secondary_channel?.channel_url === channel.id;

  const closeMenu = (open: boolean) => {
    setShowDropdown(open);
    if (!open) setMobileSection(null);
  };

  // A row that drills into a section — preventDefault keeps the menu open.
  const drillRow = (label: string, section: MobileSection) => (
    <DropDown.Item
      onSelect={(e) => {
        e.preventDefault();
        setMobileSection(section);
      }}
      className="t-flex t-items-center t-justify-between"
    >
      <span>{label}</span>
      <span className="t-flex t-rotate-180 t-text-text-30">
        <CaretLeft width="12" height="12" />
      </span>
    </DropDown.Item>
  );

  const backRow = (label: string, to: MobileSection) => (
    <DropDown.Item
      onSelect={(e) => {
        e.preventDefault();
        setMobileSection(to);
      }}
      className="t-flex t-items-center t-gap-2"
    >
      <span className="t-flex t-text-text-30">
        <CaretLeft width="12" height="12" />
      </span>
      <span className="t-text-text-30">{label}</span>
    </DropDown.Item>
  );

  const mobileMenu = (
    <div>
      {mobileSection === null && (
        <>
          <DropDown.Item
            onSelect={() =>
              channel.id &&
              dispatch(openUploadCSVModal({ channelId: channel.id }))
            }
          >
            Add open items
          </DropDown.Item>
          {channelTaskId && (
            <DropDown.Item onSelect={() => setShowBalancingReviewModal(true)}>
              Review and pay
            </DropDown.Item>
          )}
          {drillRow("Request", "request")}
          {hasAttachCart && drillRow("Attach cart", "cart")}
          {drillRow("Review", "review")}
        </>
      )}

      {mobileSection === "request" && (
        <>
          {backRow("Request", null)}
          {requestItemNodes}
          {isAdmin && drillRow("Sensitive data", "sensitive")}
        </>
      )}

      {mobileSection === "sensitive" && (
        <>
          {backRow("Sensitive data", "request")}
          {sensitiveDataItemNodes}
        </>
      )}

      {mobileSection === "review" && (
        <>
          {backRow("Review", null)}
          {reviewItemNodes}
        </>
      )}

      {mobileSection === "cart" && (
        <>
          {backRow("Attach cart", null)}
          {attachCartNode}
        </>
      )}
    </div>
  );

  const desktopMenu = (
    <div>
      <DropDown.Item
        onSelect={() =>
          channel.id && dispatch(openUploadCSVModal({ channelId: channel.id }))
        }
      >
        Add open items
      </DropDown.Item>
      {channelTaskId && (
        <DropDown.Item onSelect={() => setShowBalancingReviewModal(true)}>
          Review and pay
        </DropDown.Item>
      )}
      <DropDown.Sub>
        <DropDown.SubTrigger>Request</DropDown.SubTrigger>
        <DropDown.Portal>
          <DropDown.SubContent sideOffset={8} alignOffset={-150}>
            {requestItemNodes}
            {isAdmin && (
              <DropDown.Sub>
                <DropDown.SubTrigger>Sensitive data</DropDown.SubTrigger>
                <DropDown.Portal>
                  <DropDown.SubContent sideOffset={8} alignOffset={-150}>
                    {sensitiveDataItemNodes}
                  </DropDown.SubContent>
                </DropDown.Portal>
              </DropDown.Sub>
            )}
          </DropDown.SubContent>
        </DropDown.Portal>
      </DropDown.Sub>

      {hasAttachCart && (
        <DropDown.Sub>
          <DropDown.SubTrigger asChild>Attach cart</DropDown.SubTrigger>
          <DropDown.Portal>
            <DropDown.SubContent sideOffset={10}>
              {attachCartNode}
            </DropDown.SubContent>
          </DropDown.Portal>
        </DropDown.Sub>
      )}
      <DropDown.Sub>
        <DropDown.SubTrigger>Review</DropDown.SubTrigger>
        <DropDown.Portal>
          <DropDown.SubContent sideOffset={8} alignOffset={-30}>
            {reviewItemNodes}
          </DropDown.SubContent>
        </DropDown.Portal>
      </DropDown.Sub>
    </div>
  );

  return (
    <>
      <DropDown.Root open={showDropdown} onOpenChange={closeMenu}>
        <DropDown.Trigger asChild>
          <div className="t-flex t-justify-center t-text-neutral-50 t-items-center t-rounded">
            <PlusIcon color="currentColor" stroke="1.5" />
          </div>
        </DropDown.Trigger>
        <DropDown.Portal>
          <DropDown.Content
            className="t-w-[270px] t-max-w-[calc(100vw-24px)]"
            sideOffset={8}
            align="start"
          >
            {isSmallScreen ? mobileMenu : desktopMenu}
          </DropDown.Content>
        </DropDown.Portal>
      </DropDown.Root>
      {showUploadFileModal && (
        <UploadFile
          showModal={showUploadFileModal}
          closeModal={() => setShowUploadFileModal(false)}
          onFileSelect={onFileSelect}
          prevSelectedFiles={customAttachment as DropFile[]}
          title="Upload Documents"
          allowSingleUpload={false}
          onLinksChange={onLinksChange}
        />
      )}
      {showBalancingReviewModal && (
        <ReviewAndBalancePayment
          show={showBalancingReviewModal}
          closeModal={closeReviewAndBalancePayment}
        />
      )}
      {showBalancePayment && (
        <BalancePayment
          show={showBalancePayment}
          closeModal={() => setShowBalancePayment(false)}
        />
      )}
      <RequestSignModal
        isOpen={isRequestSignModal}
        close={closeRequestSignModal}
      />
      {isSensitiveDataRequestModal && (
        <SensitiveDataRequestModal
          formId={formId}
          isOpen={isSensitiveDataRequestModal}
          close={() => {
            closeSensitiveDataRequestModal();
            setSelectedEntityId("");
          }}
          channelUrl={channel.id!}
          entityId={entity?.uuid || selectedEntityId}
        />
      )}
      {entityPickerModal.isOpen && (
        <EntityPickerModal
          isOpen={entityPickerModal.isOpen}
          close={entityPickerModal.close}
          groupId={channelGroupId}
          selectedEntityId={selectedEntityId}
          onChange={setSelectedEntityId}
          onContinue={() => {
            entityPickerModal.close();
            openSensitiveDataRequestModal();
          }}
        />
      )}
      {requestAnswerModal.isOpen && (
        <ChatRequestAnswer {...requestAnswerModal} />
      )}
      {accountantAccessModal.isOpen && entity && (
        <RequestAccountantAccess
          {...accountantAccessModal}
          groupId={channel.data?.group_uuid as string}
          entityId={entity.uuid}
          entityName={entity.name}
          channelId={channel.id!}
        />
      )}
      {scheduleMeetingModal.isOpen && (
        <ScheduleMeetingModal
          isOpen={scheduleMeetingModal.isOpen}
          close={scheduleMeetingModal.close}
          groupId={channel.data?.group_uuid as string}
          channelUrl={channel.id ?? ""}
        />
      )}
      {reviewDraft.isOpen &&
        channel?.data?.group_uuid &&
        unsorted_folder_id && (
          <CreateDftDraft
            {...reviewDraft}
            groupId={channel?.data?.group_uuid as string}
            unsortedFolderId={unsorted_folder_id}
          />
        )}
    </>
  );
};

type EntityPickerModalProps = {
  isOpen: boolean;
  close: () => void;
  groupId?: string;
  selectedEntityId: string;
  onChange: (entityId: string) => void;
  onContinue: () => void;
};

const EntityPickerModal = ({
  isOpen,
  close,
  groupId,
  selectedEntityId,
  onChange,
  onContinue,
}: EntityPickerModalProps) => {
  const { data: groupData } = useGetGroupDataQuery(
    { groupId: groupId as string },
    { skip: !groupId }
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Select entity</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <BareEntityCombobox
            showOnlyUSEntities={false}
            disableNonUsEntities={false}
            isEntitySelected={selectedEntityId}
            value={selectedEntityId}
            onChange={onChange}
            showGoToEntity={false}
            entitiesFromProps={groupData?.entities}
          />
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button customType="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            customType="primary"
            disabled={!selectedEntityId}
            onClick={onContinue}
          >
            Continue
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
